import * as React from 'react';

import {
  LucideBellRing,
  LucideChevronDown,
  LucideChevronLeft,
  LucideChevronRight,
  LucideChevronUp,
  LucideLightbulb,
  LucideZoomIn,
  LucideZoomOut,
} from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useTranslations } from '../../i18n/useTranslations';
import { cn } from '../../lib/stylingUtils';
import { IconButton } from '../ui/buttons/IconButton';

export type CameraStreamPTZ = {
  onMove: (pan: number, tilt: number, zoom: number) => void;
  onStop: () => void;
};

export type CameraStreamFloodlight = {
  enabled: boolean;
  onToggle: () => void;
};

export type CameraStreamSiren = {
  onTrigger: () => void;
};

export type CameraStreamHandle = {
  captureFrame: (channel?: number) => HTMLCanvasElement | null;
};

export type CameraStreamProps = {
  negotiateStream: (channel: number, sdpOffer: string) => Promise<string>;
  channelCount?: number;
  channel?: number;
  ptz?: CameraStreamPTZ;
  floodlight?: CameraStreamFloodlight;
  siren?: CameraStreamSiren;
  disabled?: boolean;
  className?: string;
  ref?: React.Ref<CameraStreamHandle>;
};

type ChannelStatus = 'idle' | 'connecting' | 'connected' | 'error';

type ChannelViewProps = {
  channelIndex: number;
  negotiateStream: (channel: number, sdpOffer: string) => Promise<string>;
  disabled?: boolean;
  onVideoRef: (channel: number, el: HTMLVideoElement | null) => void;
};

const ChannelView = ({
  channelIndex,
  negotiateStream,
  disabled,
  onVideoRef,
}: ChannelViewProps) => {
  const t = useTranslations();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [status, setStatus] = React.useState<ChannelStatus>('idle');

  React.useEffect(() => {
    if (disabled) {
      setStatus('idle');
      return;
    }

    let cancelled = false;
    let pc: RTCPeerConnection | null = null;

    const connect = async () => {
      setStatus('connecting');
      try {
        pc = new RTCPeerConnection();
        pc.addTransceiver('video', { direction: 'recvonly' });

        pc.ontrack = (event) => {
          if (cancelled) return;
          if (videoRef.current && event.streams[0]) {
            videoRef.current.srcObject = event.streams[0];
            setStatus('connected');
          }
        };

        pc.oniceconnectionstatechange = () => {
          if (cancelled || !pc) return;
          if (
            pc.iceConnectionState === 'failed' ||
            pc.iceConnectionState === 'disconnected'
          ) {
            setStatus('error');
          }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const sdpAnswer = await negotiateStream(channelIndex, offer.sdp ?? '');

        if (cancelled) return;

        await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
      } catch {
        if (!cancelled) setStatus('error');
      }
    };

    connect();

    const videoEl = videoRef.current;
    return () => {
      cancelled = true;
      if (pc) {
        pc.ontrack = null;
        pc.oniceconnectionstatechange = null;
        pc.close();
      }
      if (videoEl) {
        videoEl.srcObject = null;
      }
    };
  }, [channelIndex, negotiateStream, disabled]);

  React.useEffect(() => {
    onVideoRef(channelIndex, videoRef.current);
    return () => onVideoRef(channelIndex, null);
  }, [channelIndex, onVideoRef]);

  return (
    <div className="@ChannelView relative aspect-video w-full overflow-hidden bg-neutral/10">
      {status === 'connecting' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm text-neutral/50">
            {t.components.cameraStream.loading}
          </span>
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm text-neutral/50">
            {t.components.cameraStream.unavailable}
          </span>
        </div>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={cn(
          'h-full w-full object-cover',
          status !== 'connected' && 'invisible',
        )}
      />
    </div>
  );
};

const OVERLAY_BTN = 'pointer-events-auto rounded-full bg-neutral/60 text-white';

type StreamControlsProps = {
  ptz?: CameraStreamPTZ;
  floodlight?: CameraStreamFloodlight;
  siren?: CameraStreamSiren;
};

const StreamControls = ({ ptz, floodlight, siren }: StreamControlsProps) => {
  const t = useTranslations();

  useHotkeys('ArrowLeft', () => ptz?.onMove(-1, 0, 0), {
    enabled: !!ptz,
    preventDefault: true,
  });
  useHotkeys('ArrowRight', () => ptz?.onMove(1, 0, 0), {
    enabled: !!ptz,
    preventDefault: true,
  });
  useHotkeys('ArrowUp', () => ptz?.onMove(0, 1, 0), {
    enabled: !!ptz,
    preventDefault: true,
  });
  useHotkeys('ArrowDown', () => ptz?.onMove(0, -1, 0), {
    enabled: !!ptz,
    preventDefault: true,
  });
  useHotkeys('=', () => ptz?.onMove(0, 0, 1), {
    enabled: !!ptz,
  });
  useHotkeys('-', () => ptz?.onMove(0, 0, -1), {
    enabled: !!ptz,
  });
  useHotkeys(
    'ArrowLeft,ArrowRight,ArrowUp,ArrowDown,=,-',
    () => ptz?.onStop(),
    {
      enabled: !!ptz,
      keyup: true,
      keydown: false,
    },
  );

  const hasTopControls = !!floodlight || !!siren;

  return (
    <>
      {hasTopControls && (
        <div className="pointer-events-none absolute top-2 right-2 flex gap-1">
          {floodlight && (
            <IconButton
              className={OVERLAY_BTN}
              icon={<LucideLightbulb size={16} />}
              aria-label={
                floodlight.enabled
                  ? t.components.cameraStream.floodlight.on
                  : t.components.cameraStream.floodlight.off
              }
              onPress={floodlight.onToggle}
            />
          )}
          {siren && (
            <IconButton
              className={OVERLAY_BTN}
              icon={<LucideBellRing size={16} />}
              aria-label={t.components.cameraStream.siren.trigger}
              onPress={siren.onTrigger}
            />
          )}
        </div>
      )}

      {ptz && (
        <div className="pointer-events-none absolute bottom-2 left-1/2 flex -translate-x-1/2 flex-col items-center gap-0.5">
          <IconButton
            className={OVERLAY_BTN}
            icon={<LucideChevronUp size={16} />}
            aria-label={t.components.cameraStream.ptz.up}
            onPressStart={() => ptz.onMove(0, 1, 0)}
            onPressEnd={() => ptz.onStop()}
          />
          <div className="flex gap-0.5">
            <IconButton
              className={OVERLAY_BTN}
              icon={<LucideChevronLeft size={16} />}
              aria-label={t.components.cameraStream.ptz.left}
              onPressStart={() => ptz.onMove(-1, 0, 0)}
              onPressEnd={() => ptz.onStop()}
            />
            <IconButton
              className={OVERLAY_BTN}
              icon={<LucideZoomOut size={16} />}
              aria-label={t.components.cameraStream.ptz.zoomOut}
              onPressStart={() => ptz.onMove(0, 0, -1)}
              onPressEnd={() => ptz.onStop()}
            />
            <IconButton
              className={OVERLAY_BTN}
              icon={<LucideZoomIn size={16} />}
              aria-label={t.components.cameraStream.ptz.zoomIn}
              onPressStart={() => ptz.onMove(0, 0, 1)}
              onPressEnd={() => ptz.onStop()}
            />
            <IconButton
              className={OVERLAY_BTN}
              icon={<LucideChevronRight size={16} />}
              aria-label={t.components.cameraStream.ptz.right}
              onPressStart={() => ptz.onMove(1, 0, 0)}
              onPressEnd={() => ptz.onStop()}
            />
          </div>
          <IconButton
            className={OVERLAY_BTN}
            icon={<LucideChevronDown size={16} />}
            aria-label={t.components.cameraStream.ptz.down}
            onPressStart={() => ptz.onMove(0, -1, 0)}
            onPressEnd={() => ptz.onStop()}
          />
        </div>
      )}
    </>
  );
};

export const CameraStream = ({
  negotiateStream,
  channelCount = 1,
  channel,
  ptz,
  floodlight,
  siren,
  disabled,
  className,
  ref,
}: CameraStreamProps) => {
  const videoRefs = React.useRef<Map<number, HTMLVideoElement>>(new Map());

  const channels = React.useMemo(
    () =>
      channel !== undefined
        ? [channel]
        : Array.from({ length: channelCount }, (_, i) => i),
    [channel, channelCount],
  );

  const handleVideoRef = React.useCallback(
    (ch: number, el: HTMLVideoElement | null) => {
      if (el) {
        videoRefs.current.set(ch, el);
      } else {
        videoRefs.current.delete(ch);
      }
    },
    [],
  );

  React.useImperativeHandle(
    ref,
    () => ({
      captureFrame: (targetChannel?: number) => {
        const ch = targetChannel ?? channels[0];
        const videoEl = videoRefs.current.get(ch);
        if (!videoEl || videoEl.readyState < 2) return null;

        const canvas = document.createElement('canvas');
        canvas.width = videoEl.videoWidth;
        canvas.height = videoEl.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(videoEl, 0, 0);
        return canvas;
      },
    }),
    [channels],
  );

  const gridCols = channels.length >= 2 ? 'grid-cols-2' : '';
  const hasControls = !!ptz || !!floodlight || !!siren;

  return (
    <div
      className={cn(
        '@CameraStream relative overflow-hidden rounded-lg bg-neutral/10',
        channels.length > 1 && `grid gap-1 ${gridCols}`,
        className,
      )}
    >
      {channels.map((ch) => (
        <ChannelView
          key={ch}
          channelIndex={ch}
          negotiateStream={negotiateStream}
          disabled={disabled}
          onVideoRef={handleVideoRef}
        />
      ))}

      {hasControls && !disabled && (
        <StreamControls ptz={ptz} floodlight={floodlight} siren={siren} />
      )}
    </div>
  );
};
